"use client"

import type React from "react"
import OrgLayout, { useSidebar } from "@/layouts/OrgLayout"

import { useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Plus, Trash2, Edit, Globe, Menu } from "lucide-react"
// import { mockFacilities } from "@/lib/mock-org-data"
import type { Organization, Facility } from "@/types"
import { getCSRFToken } from "@/lib/utils"

export interface OrganizationsProps {
	organizations: Organization[]
	facilities?: Facility[]
}


function Organizations({ organizations: initialOrganizations, facilities }: OrganizationsProps) {
	const [organizations, setOrganizations] = useState<Organization[]>(initialOrganizations)
	const [dialogOpen, setDialogOpen] = useState(false)
	const [editing, setEditing] = useState<Organization | null>(null)
	const [responseError, setResponseError] = useState<string | null>(null)
	const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		const formData = new FormData(e.currentTarget)
		const name = formData.get("name") as string
		const selectedFacilities = formData.getAll("facilities") as string[]
		const csrfToken = getCSRFToken()

		if (editing) {
			fetch(`/organization/${editing.id}`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					"X-CSRF-Token": csrfToken || "",
				},
				body: JSON.stringify({
					organization: {
						name,
						facility_ids: selectedFacilities.map(Number),
					},
				}),
			}).then((response) => {
				if (!response.ok) {
					setOrganizations(
						organizations.map((org) =>
							org.id === editing.id
								? {
									...org,
									name,
									facility_ids: selectedFacilities.map(Number),
									updated_at: new Date().toISOString(),
								}
								: org,
						),
					)
					response.json().then((data) => {
						if (data.error) {
							setResponseError(data.error)
						}
					})
				}
			})
		} else {
			const newOrg: Partial<Organization> = {
				name,
				facility_ids: selectedFacilities.map(Number),
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
			}
			console.log("New Organization:", newOrg)
			fetch("/organization", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-CSRF-Token": csrfToken || "",
				},
				body: JSON.stringify({ organization: newOrg }),
			})
				.then((response) => response.json())
				.then((data) => {
					if (data.id) {
						setOrganizations([...organizations, { ...newOrg, id: data.organization.id } as Organization])
					}
				})
		}
		setDialogOpen(false)
		setEditing(null)
	}

	const handleDelete = (id: number) => {
		const csrfToken = getCSRFToken()
		fetch(`/organization/${id}`, {
			method: "DELETE",
			headers: {
				"X-CSRF-Token": csrfToken || "",
			},
		}).then((response) => {
			if (response.ok) {
				setOrganizations(organizations.filter((org) => org.id !== id))
			}
			else {
				response.json().then((data) => {
					if (data.message) {
						setResponseError(data.message)
					}
				})
			}
		})
	}

	const { setShowSidebar } = useSidebar()
	return (
		<div className="space-y-6 w-full">
			<div className="flex flex-col md:flex-row md:items-center mt-2 justify-between">
				<div className="flex items-center gap-4 mb-4">
					<Menu className="md:hidden ml-2 h-6 w-6 cursor-pointer" onClick={() => setShowSidebar(true)} />
					<h2 className="text-2xl font-semibold">Organizations</h2>
					<div className="text-lg text-red-600 mt-2">{responseError}</div>
				</div>
				<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
					<DialogTrigger asChild>
						<Button onClick={() => setEditing(null)} className="w-full md:w-auto">
							<Plus className="h-4 w-4 mr-2" />
							Add Organization
						</Button>
					</DialogTrigger>
					<DialogContent>
						<form onSubmit={handleSave}>
							<DialogHeader>
								<DialogTitle>{editing ? "Edit Organization" : "Add Organization"}</DialogTitle>
								<DialogDescription>
									{editing ? "Update organization information" : "Create a new organization"}
								</DialogDescription>
							</DialogHeader>
							<div className="space-y-4 py-4">
								<div className="space-y-2">
									<Label htmlFor="name">Organization Name</Label>
									<Input id="name" name="name" defaultValue={editing?.name} placeholder="Acme Healthcare" required />
								</div>
								<div className="space-y-2">
									<Label>Assigned Facilities</Label>
									<div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
										{facilities?.map((facility) => (
											<label key={facility.id} className="flex items-center gap-2 cursor-pointer">
												<input
													type="checkbox"
													name="facilities"
													value={facility.id}
													defaultChecked={editing?.facility_ids?.includes(Number(facility.id)) ?? false}
													className="rounded"
												/>
												<span className="text-sm">{facility.name}</span>
											</label>
										))}
									</div>
								</div>
							</div>
							<DialogFooter>
								<Button type="submit">{editing ? "Update" : "Create"}</Button>
							</DialogFooter>
						</form>
					</DialogContent>
				</Dialog>

			</div>
			<div className="space-y-4">
				{organizations.map((org) => {
					const orgFacilities = facilities?.filter((f) => org.facility_ids?.includes(Number(f.id))) || []
					return (
						<Card key={org.id} className="p-6">
							<div className="flex items-start justify-between">
								<div className="flex items-center gap-3">
									<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
										<Globe className="h-5 w-5 text-primary" />
									</div>
									<div>
										<h3 className="font-semibold">{org.name}</h3>
										<p className="text-sm text-muted-foreground">{orgFacilities.length} facilities</p>
									</div>
								</div>
								<div className="flex gap-2">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => {
											setEditing(org)
											setDialogOpen(true)
										}}
									>
										<Edit className="h-4 w-4" />
									</Button>
									<Button variant="ghost" size="icon" onClick={() => handleDelete(org.id)}>
										<Trash2 className="h-4 w-4 text-destructive" />
									</Button>
								</div>
							</div>
							{orgFacilities.length > 0 && (
								<div className="mt-4 space-y-1">
									<Label className="text-xs text-muted-foreground">Facilities</Label>
									<div className="flex flex-wrap gap-2">
										{orgFacilities.map((facility) => (
											<span key={facility.id} className="px-2 py-1 text-xs bg-secondary rounded-md">
												{facility.name}
											</span>
										))}
									</div>
								</div>
							)}
						</Card>
					)
				})}
			</div>
		</div>
	)
}
Organizations.layout = (page: React.ReactNode) => <OrgLayout activeSection="organizations">{page}</OrgLayout>
export default Organizations
